<script setup>
// NOTICE: KAPTION-INTEGRATION - WhatsApp Templates Page in Campaigns context
import { ref, computed, onMounted, onActivated, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import KaptionWhatsAppPreviewEnhanced from 'dashboard/components-next/Campaigns/KaptionWhatsAppPreviewEnhanced.vue';
import WhatsAppCampaignTabs from './WhatsAppCampaignTabs.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
const { canUseFeature } = useFeaturePermissions();

// Gate: redirect if feature flag is disabled or the feature is not permitted
// feature-permissions.CAMPAIGNS.2
if (
  !isKaptionFlagEnabled('KAPTION_WHATSAPP_TEMPLATE_MANAGEMENT') ||
  !canUseFeature('whatsapp_template_management')
) {
  router.replace({ name: 'campaigns_whatsapp_index' });
}

// Get all WhatsApp inboxes then filter for Cloud API only
const allWhatsAppInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const whatsAppCloudInboxes = computed(() =>
  (allWhatsAppInboxes.value || []).filter(
    inbox => inbox.provider === 'whatsapp_cloud'
  )
);

const selectedInboxId = ref(null);
const searchQuery = ref('');
const selectedLanguage = ref('');
const selectedTemplate = ref(null);
const isSyncing = ref(false);

const hasMultipleInboxes = computed(
  () => whatsAppCloudInboxes.value.length > 1
);
const hasNoInbox = computed(() => whatsAppCloudInboxes.value.length === 0);

const uiFlags = computed(() => store.getters['whatsappTemplates/getUIFlags']);
const templates = computed(
  () => store.getters['whatsappTemplates/getTemplates']
);
const languages = computed(
  () => store.getters['whatsappTemplates/getLanguages']
);

const filteredTemplates = computed(() => {
  let result = templates.value || [];
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(tmpl => tmpl.name?.toLowerCase().includes(query));
  }
  return result;
});

const statusClass = status => {
  const s = status?.toLowerCase();
  if (s === 'approved') return 'bg-n-teal-3 text-n-teal-11';
  if (s === 'rejected') return 'bg-n-ruby-3 text-n-ruby-11';
  return 'bg-n-amber-3 text-n-amber-11';
};

const statusLabel = status => {
  const s = status?.toLowerCase();
  if (s === 'approved') return t('WHATSAPP_TEMPLATE_MGMT.LIST.STATUS.APPROVED');
  if (s === 'rejected') return t('WHATSAPP_TEMPLATE_MGMT.LIST.STATUS.REJECTED');
  return t('WHATSAPP_TEMPLATE_MGMT.LIST.STATUS.PENDING');
};

const fetchTemplates = async () => {
  if (!selectedInboxId.value) return;
  await store.dispatch('whatsappTemplates/fetch', {
    inboxId: selectedInboxId.value,
    language: selectedLanguage.value || undefined,
    excludeKaption: true,
  });
};

const syncTemplates = async () => {
  if (!selectedInboxId.value) return;
  isSyncing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', selectedInboxId.value);
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.LIST.ACTIONS.REFRESH_SUCCESS'));
    await fetchTemplates();
  } catch {
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.LIST.ACTIONS.REFRESH_ERROR'));
  } finally {
    isSyncing.value = false;
  }
};

const selectTemplate = template => {
  selectedTemplate.value = template;
};

// Auto-select first Cloud inbox when available
watch(
  whatsAppCloudInboxes,
  inboxes => {
    if (inboxes.length > 0 && !selectedInboxId.value) {
      selectedInboxId.value = inboxes[0].id;
    }
  },
  { immediate: true }
);

// Fetch templates when inbox changes
watch(selectedInboxId, () => {
  selectedTemplate.value = null;
  fetchTemplates();
});

watch(selectedLanguage, () => {
  fetchTemplates();
});

onMounted(() => {
  // NOTICE: KAPTION-INTEGRATION
  // Refresh inboxes so newly created templates (inserted into JSONB) are visible.
  store.dispatch('inboxes/get');
  if (selectedInboxId.value) {
    fetchTemplates();
  }
});

// NOTICE: KAPTION-INTEGRATION — keep-alive re-activation
// When navigating back from the create page, onMounted does NOT fire
// because <keep-alive> in CampaignsPageRouteView caches this component.
// onActivated fires every time the cached component becomes active again.
onActivated(() => {
  if (selectedInboxId.value) {
    fetchTemplates();
  }
});
</script>

<template>
  <CampaignLayout :header-title="t('WHATSAPP_TEMPLATE_MGMT.TITLE')">
    <template #header-action>
      <div class="flex gap-2">
        <NextButton
          :disabled="isSyncing || hasNoInbox"
          faded
          xs
          @click="syncTemplates"
        >
          <Icon
            icon="i-lucide-refresh-ccw"
            class="size-3.5 mr-1"
            :class="{ 'animate-spin': isSyncing }"
          />
          {{ t('WHATSAPP_TEMPLATE_MGMT.LIST.ACTIONS.REFRESH') }}
        </NextButton>
        <router-link
          v-if="!hasNoInbox"
          :to="{ name: 'campaigns_whatsapp_templates_new' }"
          class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-n-brand hover:bg-n-brand-hover rounded-lg transition-colors shadow-sm"
        >
          <span class="i-lucide-plus w-3.5 h-3.5" />
          {{ t('WHATSAPP_TEMPLATE_MGMT.LIST.ACTIONS.CREATE') }}
        </router-link>
      </div>
    </template>

    <!-- Tabs -->
    <WhatsAppCampaignTabs class="mb-4" />

    <!-- No Inbox Empty State -->
    <div
      v-if="hasNoInbox"
      class="flex flex-col items-center justify-center py-16 gap-3"
    >
      <Icon
        icon="i-lucide-message-square-warning"
        class="size-10 text-n-slate-9"
      />
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('WHATSAPP_TEMPLATE_MGMT.NO_INBOX.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ t('WHATSAPP_TEMPLATE_MGMT.NO_INBOX.DESCRIPTION') }}
      </p>
    </div>

    <template v-else>
      <!-- Inbox Selector (if multiple) -->
      <div v-if="hasMultipleInboxes" class="mb-4">
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('WHATSAPP_TEMPLATE_MGMT.INBOX_SELECTOR.LABEL') }}
        </label>
        <select
          v-model="selectedInboxId"
          class="w-64 h-9 px-2.5 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand cursor-pointer"
        >
          <option
            v-for="inbox in whatsAppCloudInboxes"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }} ({{ inbox.phone_number }})
          </option>
        </select>
      </div>

      <!-- Filters -->
      <div class="flex gap-3 items-center mb-4">
        <div
          class="flex flex-1 gap-1 items-center px-2.5 py-0 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus-within:outline-n-brand dark:focus-within:outline-n-brand"
        >
          <Icon icon="i-lucide-search" class="text-n-slate-11 size-4" />
          <input
            v-model="searchQuery"
            type="search"
            :placeholder="
              t('WHATSAPP_TEMPLATE_MGMT.LIST.FILTER.SEARCH_PLACEHOLDER')
            "
            class="reset-base w-full h-9 bg-transparent text-n-slate-12 !text-sm !outline-0"
          />
        </div>
        <select
          v-model="selectedLanguage"
          class="w-48 h-9 px-2.5 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand cursor-pointer"
        >
          <option value="">
            {{ t('WHATSAPP_TEMPLATE_MGMT.LIST.FILTER.ALL_LANGUAGES') }}
          </option>
          <option v-for="lang in languages" :key="lang" :value="lang">
            {{ lang }}
          </option>
        </select>
      </div>

      <!-- Content area: Table + Preview side-by-side -->
      <div class="flex gap-4">
        <!-- Template List Table -->
        <div
          class="flex-1 overflow-auto rounded-lg bg-n-background outline outline-1 outline-n-container"
        >
          <div
            v-if="uiFlags.isFetching"
            class="flex justify-center items-center py-16"
          >
            <span class="text-sm text-n-slate-11">
              {{ t('WHATSAPP_TEMPLATE_MGMT.LIST.LOADING') }}
            </span>
          </div>

          <div
            v-else-if="!filteredTemplates.length"
            class="flex flex-col justify-center items-center py-16 gap-2"
          >
            <Icon icon="i-lucide-file-text" class="size-8 text-n-slate-9" />
            <span class="text-sm text-n-slate-11">
              {{
                searchQuery || selectedLanguage
                  ? t('WHATSAPP_TEMPLATE_MGMT.LIST.EMPTY_FILTERED')
                  : t('WHATSAPP_TEMPLATE_MGMT.LIST.EMPTY')
              }}
            </span>
          </div>

          <table v-else class="w-full">
            <thead>
              <tr
                class="border-b border-n-weak text-left text-xs font-medium text-n-slate-11 uppercase"
              >
                <th class="px-4 py-3">
                  {{ t('WHATSAPP_TEMPLATE_MGMT.LIST.TABLE.NAME') }}
                </th>
                <th class="px-4 py-3">
                  {{ t('WHATSAPP_TEMPLATE_MGMT.LIST.TABLE.CATEGORY') }}
                </th>
                <th class="px-4 py-3">
                  {{ t('WHATSAPP_TEMPLATE_MGMT.LIST.TABLE.LANGUAGE') }}
                </th>
                <th class="px-4 py-3">
                  {{ t('WHATSAPP_TEMPLATE_MGMT.LIST.TABLE.STATUS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="template in filteredTemplates"
                :key="template.id || template.name + template.language"
                class="border-b border-n-weak cursor-pointer hover:bg-n-alpha-2 dark:hover:bg-n-solid-2 transition-colors"
                :class="{
                  'bg-n-alpha-2 dark:bg-n-solid-2':
                    selectedTemplate?.name === template.name &&
                    selectedTemplate?.language === template.language,
                }"
                @click="selectTemplate(template)"
              >
                <td class="px-4 py-3">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ template.name }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="text-sm text-n-slate-11">
                    {{ template.category }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span
                    class="inline-block px-2 py-0.5 text-xs rounded bg-n-slate-3 text-n-slate-12"
                  >
                    {{ template.language }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span
                    class="inline-block px-2 py-0.5 text-xs rounded"
                    :class="statusClass(template.status)"
                  >
                    {{ statusLabel(template.status) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Preview Panel -->
        <div
          class="w-80 flex-shrink-0 rounded-lg bg-n-background outline outline-1 outline-n-container overflow-hidden"
        >
          <div class="px-4 py-3 border-b border-n-weak">
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.TITLE') }}
            </h4>
          </div>
          <KaptionWhatsAppPreviewEnhanced
            :template="selectedTemplate"
            :variables="[]"
            :resolved-values="{}"
            mode="render"
            :loading="false"
            :bare="false"
            truncate
            company-name="Business"
            class="w-full h-[520px]"
          />
        </div>
      </div>
    </template>
  </CampaignLayout>
</template>
